import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { invariant } from "@searchui-searchforms/shared/utils";
import { useUIComponentLoaded } from "@/shared/hooks/useUIComponentLoaded";
import { createSafeContext } from "@/shared/utils/createSafeContext";
import type { ExtendedValidationFieldConfig } from "./types";
import { useGenerateConfigWithPattern } from "./useGenerateConfigWithPattern";

type CleanupFn = () => void;

interface ValidatorContextValue {
    attachFieldValidation: (fieldId: string, config: ExtendedValidationFieldConfig) => CleanupFn;
    resetAllFieldErrors: () => void;
}

const [ValidatorProviderBase, useValidatorContext] = createSafeContext<ValidatorContextValue>("FormValidatorContext");

interface ValidatorProviderProps {
    children: ReactNode;
    formId: string;
}

export const ValidatorProvider = ({ children, formId }: ValidatorProviderProps) => {
    const validatorRef = useRef<UiValidatorInstance | null>(null);
    // Keep track of calls which may happen before validatorRef.current is attached
    const queue = useRef<Map<string, ExtendedValidationFieldConfig>>(new Map());
    const generateConfigWithPattern = useGenerateConfigWithPattern();

    const validatorLibLoaded = useUIComponentLoaded("validator");

    const attachFieldValidation = useCallback(
        (fieldId: string, extendedConfig: ExtendedValidationFieldConfig) => {
            if (validatorRef.current) {
                const config = generateConfigWithPattern(extendedConfig, validatorRef.current);
                const modifyingField = !!validatorRef.current!.fields[fieldId];

                // Prevent immediate validation for newly registered fields that already have a
                // DOM value (e.g. populated programmatically by a linked shortcut field).
                // core.js _initField validates on registration when the element has a value,
                // showing errors before the user interacts with the field.
                // Temporarily clearing the value suppresses this; blur-based validation still works.
                // Safe: programmatic .value assignment does not fire change/input events.
                let savedValue: string | undefined;
                if (!modifyingField) {
                    const inputEl = document.getElementById(fieldId) as HTMLInputElement | null;
                    if (inputEl && inputEl.value) {
                        savedValue = inputEl.value;
                        inputEl.value = "";
                    }
                }

                validatorRef.current!.addField(fieldId, config, modifyingField);

                if (savedValue !== undefined) {
                    const inputEl = document.getElementById(fieldId) as HTMLInputElement | null;
                    if (inputEl) {
                        inputEl.value = savedValue;
                    }
                }
            } else {
                queue.current.set(fieldId, extendedConfig);
            }
            return () => {
                // If we try to remove non-registered field, validator would throw an error
                if (validatorRef.current?.fields[fieldId]) {
                    validatorRef.current.removeFields(fieldId);
                }
            };
        },
        [generateConfigWithPattern],
    );

    const resetAllFieldErrors = useCallback(() => {
        if (validatorRef.current) {
            const fieldIds = Object.keys(validatorRef.current.fields);
            for (const fieldId of fieldIds) {
                validatorRef.current.updateValidationMessage(fieldId, true);
            }
        }
    }, []);

    useEffect(() => {
        if (validatorLibLoaded && !validatorRef.current) {
            const formNode = document.getElementById(formId);
            invariant(formNode, "Did not find form element");
            // Pass detached dummy elements for submitButton and resetButton to prevent
            // the validator from directly manipulating real DOM buttons' disabled state.
            // The validator uses querySelector('[type="submit"]') by default, which only
            // finds the first button — causing inconsistency when multiple submit buttons exist.
            // React manages button states; the validator should only handle field validation.
            const dummySubmit = document.createElement("button");
            const dummyReset = document.createElement("button");

            validatorRef.current = window.ui.validator(formNode, {
                fields: {},
                allowMultipleSubmissions: true,
                submitButton: dummySubmit,
                resetButton: dummyReset,
                onSubmit: ({ valid }: { valid: boolean }) => valid,
            });
            if (queue.current.size) {
                queue.current.forEach((config, fieldId) => {
                    attachFieldValidation(fieldId, config);
                });
                queue.current.clear();
            }
            return () => {
                validatorRef.current?.destroy();
                validatorRef.current = null;
            };
        }
    }, [attachFieldValidation, formId, validatorLibLoaded]);

    const providerValue = useMemo(
        () => ({ attachFieldValidation, resetAllFieldErrors }),
        [attachFieldValidation, resetAllFieldErrors],
    );

    return <ValidatorProviderBase value={providerValue}>{children}</ValidatorProviderBase>;
};

export { useValidatorContext };
